/**
 * The second level of Settings navigation.
 *
 * Some tabs carry three or four unrelated areas. Stacked, they opened on a
 * wall of forms with everything below the fold invisible (Aleix, 2026-09-11:
 * "very vertical, very poor UI"). Siblings read as sub-tabs, and the choice
 * rides in the URL (`?tab=organization&section=branding`) so a refresh, the
 * back button and a shared link all land where you were.
 *
 * Deliberately a different shape from the tabs above it — pills on a tinted
 * track rather than an underline — so the two levels never read as one row.
 */
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { EASE_OUT } from '../../lib/motion';
import { cn } from '../../lib/utils';

export interface SubTabSection {
  id: string;
  label: string;
  /** Optional — the tab bar above already carries icons, and a second row of
   *  them reads as clutter rather than as navigation (Aleix, 2026-09-11). */
  icon?: React.ComponentType<{ className?: string }>;
  /** One line under the bar saying what this area is for. */
  hint: string;
  /** Renders a padlock on the tab — the area exists but the plan gates it. */
  locked?: boolean;
  lockedHint?: string;
}

/**
 * Reads and writes the `section` query parameter for one tab's sub-sections.
 * Returns the current section id (falling back to the first) and a setter.
 */
export function useSettingsSection(
  tab: string,
  sections: readonly SubTabSection[],
  preferred?: string
): [string, (id: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('section');
  const known = (value: string | null | undefined) =>
    Boolean(value) && sections.some((s) => s.id === value);

  const current = known(requested)
    ? (requested as string)
    : known(preferred)
      ? (preferred as string)
      : sections[0].id;

  const select = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    next.set('section', id);
    // Replace, not push: flipping between siblings shouldn't stack up history.
    setSearchParams(next, { replace: true });
  };

  return [current, select];
}

interface Props {
  ariaLabel: string;
  sections: readonly SubTabSection[];
  current: string;
  onSelect: (id: string) => void;
  /** Shared layoutId for the sliding pill — unique per bar on screen. */
  pillId: string;
}

const SettingsSubTabs: React.FC<Props> = ({
  ariaLabel,
  sections,
  current,
  onSelect,
  pillId,
}) => {
  const active = sections.find((s) => s.id === current) ?? sections[0];

  return (
    <>
      <div
        role="tablist"
        aria-label={ariaLabel}
        className="mb-4 inline-flex flex-wrap gap-1 rounded-full bg-text-primary/[0.05] p-1"
      >
        {sections.map((section) => {
          const Icon = section.icon;
          const selected = section.id === current;
          return (
            <button
              key={section.id}
              type="button"
              role="tab"
              id={`section-tab-${section.id}`}
              aria-selected={selected}
              aria-controls={`section-panel-${section.id}`}
              onClick={() => onSelect(section.id)}
              className={cn(
                'relative inline-flex items-center gap-2 rounded-full px-4 py-2',
                'text-sm font-semibold transition-colors duration-fast',
                'focus-ring active:scale-[0.98]',
                selected
                  ? 'text-text-primary'
                  : 'text-text-secondary hover:text-text-primary'
              )}
            >
              {selected && (
                <motion.span
                  layoutId={pillId}
                  aria-hidden="true"
                  className="absolute inset-0 rounded-full bg-surface-elevated shadow-sm"
                  transition={{ duration: 0.2, ease: EASE_OUT }}
                />
              )}
              {Icon && <Icon className="relative h-4 w-4" aria-hidden="true" />}
              <span className="relative">{section.label}</span>
              {section.locked && (
                <LockClosedIcon
                  className="relative h-3.5 w-3.5 text-text-tertiary"
                  aria-label={section.lockedHint ?? 'Not included in your plan'}
                />
              )}
            </button>
          );
        })}
      </div>

      <p className="mb-6 text-sm text-text-secondary">{active.hint}</p>
    </>
  );
};

export default SettingsSubTabs;
